import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface BillActivityLog {
  id: string;
  workspace_id: string;
  bill_id: string | null;
  stage: string;
  action: string;
  action_type: 'stage_change' | 'assignment' | 'parsing' | 'validation' | 'approval' | 'rejection' | 'edit' | 'note' | 'system';
  description: string;
  old_value?: any;
  new_value?: any;
  user_id?: string;
  user_name?: string;
  ip_address?: string;
  user_agent?: string;
  duration_ms?: number;
  created_at: string;
}

interface UseBillActivityLogsOptions {
  stage?: string;
  billId?: string;
  actionType?: string;
  days?: number;
}

export function useBillActivityLogs(options: UseBillActivityLogsOptions = {}) {
  const { currentWorkspace } = useWorkspace();
  const { stage, billId, actionType, days = 15 } = options;

  return useQuery({
    queryKey: ['bill-activity-logs', currentWorkspace?.id, stage, billId, actionType, days],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];

      let query = supabase
        .from('bill_activity_logs')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .gte('created_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (stage) {
        query = query.eq('stage', stage);
      }

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      if (actionType && actionType !== 'all') {
        query = query.eq('action_type', actionType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as BillActivityLog[];
    },
    enabled: !!currentWorkspace?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Helper function to log activity from client-side
export async function logBillActivity(
  workspaceId: string,
  billId: string | null,
  stage: string,
  action: string,
  actionType: string,
  description: string,
  oldValue?: any,
  newValue?: any
) {
  const { error } = await supabase.rpc('log_bill_activity', {
    p_workspace_id: workspaceId,
    p_bill_id: billId,
    p_stage: stage,
    p_action: action,
    p_action_type: actionType,
    p_description: description,
    p_old_value: oldValue || null,
    p_new_value: newValue || null,
  });

  if (error) {
    console.error('Failed to log activity:', error);
  }
}
